import { Fragment, useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Eye, FolderOpen, Info, RotateCcw, Share2, Star, X, type LucideIcon } from "@fg/icons";
import { suiteApp, type SuiteAppId } from "@fg/ui-theme";
import type { DriveItem } from "@/api/driveApi";
import { sharingApi } from "@/api/sharingApi";
import { useAncestors, useItemActivity, useItems, useUploadedBy, useVersions } from "@/api/hooks";
import { useAccessToken } from "@/auth/useAccessToken";
import { useMe, type Me } from "@/auth/useMe";
import { PersonAvatar } from "@/components/PersonAvatar";
import { formatBytes, formatDateTime, formatDetailDate, formatRelative, plural } from "@/lib/format";
import { OFFICE_APP_NAME, categoryOf, officeKindOf, typeLabel } from "@/lib/fileTypes";
import { modifierLabel, nameFromEmail, sameEmail } from "@/lib/people";
import { usePersistentState } from "@/lib/storage";
import { cn } from "@/lib/utils";
import { useDriveActions } from "../driveActions";
import { ItemThumb } from "../items/ItemIcon";
import { canDownload, canEdit, owns } from "../items/menuItems";
import { ItemActivity } from "../activity/ItemActivity";
import { activitySentence } from "../activity/activityText";
import { CommentsView } from "../comments/CommentsPanel";
import type { DriveVariant } from "../types";

export type DetailsTab = "details" | "activity" | "comments";
const isDetailsTab = (v: unknown): v is DetailsTab => v === "details" || v === "activity" || v === "comments";
const TABS: { id: DetailsTab; label: string }[] = [
    { id: "details", label: "Details" },
    { id: "activity", label: "Activity" },
    { id: "comments", label: "Comments" }
];

/**
 * Right-hand details card: preview, Open in <app>, Share, who has access,
 * key facts and recent activity, with Activity / Comments tabs. A 300 px
 * card from lg up, a slide-over sheet below. Shows the single selected item,
 * or the current folder when nothing is selected.
 */
export function DetailsPane({
    item,
    selectionCount,
    variant,
    onClose,
    requestedTab,
    onCommentsChanged,
    onCommentCount
}: {
    item: DriveItem | null;
    selectionCount: number;
    variant: DriveVariant;
    onClose: () => void;
    /** Switch to this tab ("Comment" in the item menu); a new nonce repeats it and focuses the composer. */
    requestedTab?: { tab: DetailsTab; nonce: number } | null;
    /** A comment was posted / edited / resolved — refresh badges and activity. */
    onCommentsChanged?: () => void;
    /** The Comments tab's latest open-thread count for an item. */
    onCommentCount?: (itemId: string, open: number) => void;
}) {
    // Remembered across items and sessions, like Google Drive's details panel.
    const [tab, setTab] = usePersistentState<DetailsTab>("drive.details.tab", "details", isDetailsTab);
    useEffect(() => {
        if (requestedTab) setTab(requestedTab.tab);
        // Only a new request switches tabs; setTab is a state setter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedTab]);
    return (
        <>
            <div className="drive-fade-in fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={onClose} aria-hidden />
            <aside
                aria-label="Details"
                data-drive-overlay-mobile=""
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        e.stopPropagation();
                        onClose();
                    }
                }}
                className="drive-slide-left-mobile fixed inset-y-0 right-0 z-40 flex w-[min(22rem,100vw)] flex-col overflow-hidden bg-card text-card-foreground shadow-2xl lg:static lg:z-auto lg:w-[300px] lg:shrink-0 lg:rounded-lg lg:shadow-[var(--drive-shadow-4)]"
            >
                <header className="flex h-11 shrink-0 items-center gap-1 border-b border-[var(--drive-line)] pl-2 pr-1.5">
                    {item ? (
                        <DetailsTabs value={tab} onChange={setTab} />
                    ) : (
                        <h2 className="min-w-0 flex-1 px-2 text-sm font-semibold">Details</h2>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close details"
                        title="Close details"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[4px] text-[var(--drive-text-2)] hover:bg-[var(--drive-hover)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </header>
                {item ? (
                    <div
                        id={`details-panel-${tab}`}
                        role="tabpanel"
                        aria-labelledby={`details-tab-${tab}`}
                        className="flex min-h-0 flex-1 flex-col"
                    >
                        {tab === "details" ? (
                            <DetailsBody key={item.id} item={item} variant={variant} onSeeActivity={() => setTab("activity")} />
                        ) : tab === "activity" ? (
                            <div className="min-h-0 flex-1 overflow-y-auto">
                                <ItemActivity key={item.id} item={item} />
                            </div>
                        ) : (
                            <CommentsView
                                key={item.id}
                                fileId={item.id}
                                active
                                autoFocus={requestedTab?.tab === "comments"}
                                focusKey={requestedTab?.nonce}
                                onChanged={onCommentsChanged}
                                onOpenCountChange={(open) => onCommentCount?.(item.id, open)}
                            />
                        )}
                    </div>
                ) : (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center text-sm text-[var(--drive-text-2)]">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--drive-subtle)]">
                            <Info className="h-7 w-7" aria-hidden />
                        </div>
                        {selectionCount > 1 ? `${selectionCount} items selected` : "Select a file or folder to see its details."}
                    </div>
                )}
            </aside>
        </>
    );
}

/** "Details | Activity | Comments" — an ARIA tablist with ←/→/Home/End between tabs. */
function DetailsTabs({ value, onChange }: { value: DetailsTab; onChange: (t: DetailsTab) => void }) {
    const refs = useRef<Record<DetailsTab, HTMLButtonElement | null>>({ details: null, activity: null, comments: null });
    const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        const i = TABS.findIndex((t) => t.id === value);
        let next: number | null = null;
        if (e.key === "ArrowRight") next = (i + 1) % TABS.length;
        else if (e.key === "ArrowLeft") next = (i - 1 + TABS.length) % TABS.length;
        else if (e.key === "Home") next = 0;
        else if (e.key === "End") next = TABS.length - 1;
        if (next === null) return;
        e.preventDefault();
        const id = TABS[next]!.id;
        onChange(id);
        refs.current[id]?.focus();
    };
    return (
        <div role="tablist" aria-label="Details pane" onKeyDown={onKeyDown} className="flex min-w-0 flex-1 gap-3 self-stretch px-2">
            {TABS.map((t) => {
                const selected = t.id === value;
                return (
                    <button
                        key={t.id}
                        ref={(el) => { refs.current[t.id] = el; }}
                        id={`details-tab-${t.id}`}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        aria-controls={`details-panel-${t.id}`}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onChange(t.id)}
                        className={cn(
                            "relative -mb-px border-b-2 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                            selected
                                ? "border-primary font-semibold text-foreground"
                                : "border-transparent text-[var(--drive-text-2)] hover:text-foreground"
                        )}
                    >
                        {t.label}
                    </button>
                );
            })}
        </div>
    );
}

const btnBase =
    "inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-[4px] border px-3 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card";
const btnPrimary = cn(btnBase, "border-primary bg-primary text-primary-foreground hover:bg-[var(--drive-primary-hover)]");
const btnOutline = cn(btnBase, "border-[var(--drive-line-strong)] bg-card hover:bg-[var(--drive-hover)]");
const btnIcon = cn(btnOutline, "w-[30px] justify-center px-0");

/** The primary action: Open in Docs / Sheets / Slides / PDF, or plain Open. */
function openAction(item: DriveItem): { label: string; icon: LucideIcon; app: boolean } {
    if (item.kind === "Folder") return { label: "Open", icon: FolderOpen, app: false };
    const office = officeKindOf(item);
    if (office) {
        const id: SuiteAppId = office === "docx" ? "docs" : office === "xlsx" ? "sheets" : "slides";
        return { label: `Open in ${OFFICE_APP_NAME[office]}`, icon: suiteApp(id).icon, app: true };
    }
    // PDFs open in the PDF app, which needs the bytes — not for no-download shares.
    if (categoryOf(item) === "pdf" && canDownload(item)) return { label: "Open in PDF", icon: suiteApp("pdf").icon, app: true };
    return { label: "Open", icon: Eye, app: false };
}

function DetailsBody({ item, variant, onSeeActivity }: { item: DriveItem; variant: DriveVariant; onSeeActivity: () => void }) {
    const actions = useDriveActions();
    const me = useMe();
    const isFolder = item.kind === "Folder";
    const inBin = variant === "trash" || item.isTrashed === true;
    const children = useItems(item.id, { enabled: isFolder && !inBin });
    // Files that came in through "Request files" say who sent them.
    const uploadedBy = useUploadedBy(!isFolder ? item.id : null);
    const showVersions = !isFolder && !inBin && canEdit(item);
    const versions = useVersions(showVersions ? item.id : null);
    const open = openAction(item);

    return (
        <div className="min-h-0 flex-1 overflow-y-auto">
            <ItemThumb item={item} size="hero" />
            <div className="flex flex-col gap-3.5 px-4 py-3.5">
                <div className="min-w-0">
                    <h3 className="break-words text-[15px] font-semibold leading-5">{item.name}</h3>
                    <div className="mt-0.5 text-xs text-[var(--drive-text-2)]">
                        {inBin ? (
                            "In the bin"
                        ) : item.ownerEmail ? (
                            <>
                                Shared by <b className="font-semibold text-foreground">{nameFromEmail(item.ownerEmail)}</b>
                            </>
                        ) : (
                            <>
                                In <LocationPath parentId={item.parentId} />
                            </>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {inBin ? (
                        <button type="button" className={btnPrimary} onClick={() => void actions.restore([item])}>
                            <RotateCcw className="h-4 w-4" aria-hidden /> Restore
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className={btnPrimary}
                                onClick={() => (open.app ? actions.openInApp(item) : isFolder ? actions.open(item) : actions.preview(item))}
                            >
                                <open.icon className="h-4 w-4" aria-hidden /> {open.label}
                            </button>
                            {canEdit(item) ? (
                                <button type="button" className={btnOutline} onClick={() => actions.share(item)}>
                                    <Share2 className="h-4 w-4" aria-hidden /> Share
                                </button>
                            ) : null}
                            {canDownload(item) ? (
                                <button type="button" className={btnIcon} onClick={() => void actions.download([item])} aria-label="Download" title="Download">
                                    <Download className="h-4 w-4" aria-hidden />
                                </button>
                            ) : null}
                            {owns(item) ? (
                                <button
                                    type="button"
                                    className={btnIcon}
                                    onClick={() => actions.toggleStar([item])}
                                    aria-pressed={Boolean(item.isFavorite)}
                                    aria-label={item.isFavorite ? "Starred" : "Star"}
                                    title={item.isFavorite ? "Remove from Starred" : "Add to Starred"}
                                >
                                    <Star className={cn("h-4 w-4", item.isFavorite && "fill-amber-400 text-amber-500")} aria-hidden />
                                </button>
                            ) : null}
                        </>
                    )}
                </div>

                {!inBin ? <WhoHasAccess item={item} me={me} /> : null}

                <dl className="grid grid-cols-[90px_minmax(0,1fr)] gap-x-2 gap-y-1 text-[12.5px]">
                    <Row label="Type">{typeLabel(item)}</Row>
                    {isFolder ? (
                        !inBin ? <Row label="Contains">{children.data ? plural(children.data.length, "item") : "…"}</Row> : null
                    ) : (
                        <Row label="Size"><span className="tabular-nums">{formatBytes(item.sizeBytes)}</span></Row>
                    )}
                    {item.ownerEmail ? <Row label="Owner">{nameFromEmail(item.ownerEmail)}</Row> : null}
                    <Row label="Modified">
                        <span className="tabular-nums">{formatDetailDate(item.updatedAt)}</span>
                        {item.modifiedBy ? ` by ${modifierLabel(item.modifiedBy, me.email)}` : ""}
                    </Row>
                    <Row label="Created"><span className="tabular-nums">{formatDetailDate(item.createdAt)}</span></Row>
                    {!isFolder ? <Row label="Last opened">{item.lastAccessedAt ? formatDetailDate(item.lastAccessedAt) : "Not yet"}</Row> : null}
                    {showVersions ? (
                        <Row label="Versions">
                            {versions.data ? versions.data.length : "…"} ·{" "}
                            <button type="button" onClick={() => actions.history(item)} className="rounded font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                                History
                            </button>
                        </Row>
                    ) : null}
                    {uploadedBy.data ? (
                        <Row label="Uploaded by">
                            {uploadedBy.data.name} ({uploadedBy.data.email})
                            <span className="block text-[var(--drive-text-3)]">
                                via “{uploadedBy.data.requestTitle}” · {formatDateTime(uploadedBy.data.uploadedAt)}
                            </span>
                        </Row>
                    ) : null}
                    {inBin ? <Row label="Moved to bin">{formatDetailDate(item.trashedAt)}</Row> : null}
                </dl>

                {!inBin ? <RecentActivity item={item} me={me} onSeeAll={onSeeActivity} /> : null}
            </div>
        </div>
    );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <>
            <dt className="text-[var(--drive-text-3)]">{label}</dt>
            <dd className="m-0 break-words">{children}</dd>
        </>
    );
}

/** Owner + everyone granted access, as an avatar stack; owners/editors manage it. */
function WhoHasAccess({ item, me }: { item: DriveItem; me: Me }) {
    const actions = useDriveActions();
    const token = useAccessToken();
    // Same key as ShareDialog, so changes made there show up here.
    const sharing = useQuery({
        queryKey: ["drive", "sharing", item.id, token],
        queryFn: () => sharingApi.get(item.id, token),
        enabled: Boolean(token),
        retry: false
    });
    if (sharing.isError) return null;
    const data = sharing.data;
    const emails = data ? [data.ownerEmail, ...data.people.map((p) => p.email).filter((e) => !sameEmail(e, data.ownerEmail))] : [];
    const shown = emails.slice(0, 5);
    const extra = emails.length - shown.length;
    const alone = data !== undefined && data.people.length === 0;
    const names = emails.map((e) => (sameEmail(e, me.email) ? "You" : nameFromEmail(e)));

    return (
        <div>
            <div className="mb-1.5 text-xs font-semibold">Who has access</div>
            <div className="flex min-h-7 items-center gap-2.5">
                {sharing.isPending ? (
                    <div className="flex" aria-busy="true" aria-label="Loading access">
                        {[0, 1, 2].map((i) => (
                            <span key={i} className="-mr-1.5 h-7 w-7 animate-pulse rounded-full bg-muted ring-2 ring-card" />
                        ))}
                    </div>
                ) : (
                    <ul className="flex" aria-label={`People with access: ${names.join(", ")}`}>
                        {shown.map((email) => (
                            <li key={email} className="-mr-1.5" title={sameEmail(email, me.email) ? `${me.name} (you)` : email}>
                                <PersonAvatar
                                    email={email}
                                    name={sameEmail(email, me.email) ? me.name : undefined}
                                    mine={sameEmail(email, me.email)}
                                    size={28}
                                    ring
                                />
                            </li>
                        ))}
                        {extra > 0 ? (
                            <li className="-mr-1.5 flex h-7 min-w-7 items-center justify-center rounded-full bg-[var(--drive-subtle)] px-1 text-[11px] font-semibold ring-2 ring-card">
                                +{extra}
                            </li>
                        ) : null}
                    </ul>
                )}
                {alone ? (
                    <span className="ml-1.5 text-xs text-[var(--drive-text-2)]">
                        {item.isShared ? "Shared by link" : sameEmail(data?.ownerEmail, me.email) ? "Only you" : "Owner only"}
                    </span>
                ) : null}
                {data?.canManage ? (
                    <button
                        type="button"
                        onClick={() => actions.share(item)}
                        className="ml-auto rounded text-xs font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        Manage access
                    </button>
                ) : null}
            </div>
        </div>
    );
}

/** The latest three entries of the item's timeline; the Activity tab has the rest. */
function RecentActivity({ item, me, onSeeAll }: { item: DriveItem; me: Me; onSeeAll: () => void }) {
    const activity = useItemActivity(item.id);
    const rows = activity.data?.pages[0]?.items.slice(0, 3) ?? [];
    if (rows.length === 0) return null;
    return (
        <div>
            <div className="mb-2 flex items-center text-xs font-semibold">
                Activity
                <button
                    type="button"
                    onClick={onSeeAll}
                    className="ml-auto rounded font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    See all
                </button>
            </div>
            <ol className="flex flex-col gap-2.5 text-[12.5px]">
                {rows.map((a) => {
                    const mine = sameEmail(a.actorEmail, me.email);
                    return (
                        <li key={a.id} className="grid grid-cols-[22px_minmax(0,1fr)] gap-2">
                            <PersonAvatar email={a.actorEmail} name={mine ? me.name : undefined} mine={mine} />
                            <span className="leading-snug text-foreground/85">
                                {activitySentence(a, me.email)}{" "}
                                <time dateTime={a.createdAt} title={formatDateTime(a.createdAt)} className="text-[var(--drive-text-3)]">
                                    · {formatRelative(a.createdAt)}
                                </time>
                            </span>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}

function LocationPath({ parentId }: { parentId: string | null }) {
    const ancestors = useAncestors(parentId);
    const chain = parentId ? ancestors.data ?? [] : [];
    const link = "rounded font-semibold text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
    return (
        <span className="inline">
            <Link to="/" className={link}>My Drive</Link>
            {parentId && ancestors.isPending ? <span> › …</span> : null}
            {chain.map((f) => (
                <Fragment key={f.id}>
                    <span className="text-[var(--drive-text-3)]"> › </span>
                    <Link to={`/folder/${encodeURIComponent(f.id)}`} className={link}>{f.name}</Link>
                </Fragment>
            ))}
        </span>
    );
}
